import { Zap } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Skeleton, SkeletonChart, SkeletonStyles, SkeletonText } from "@/shared/ui/skeleton";
import { Card, Mono, T } from "@/shared/ui/DesignLib";

type PortalSkeletonTab =
  | "overview"
  | "connector"
  | "watches"
  | "account"
  | "notifications"
  | "plans"
  | "live-options"
  | "zdte";

const titleByTab: Record<PortalSkeletonTab, string> = {
  overview: "Overview",
  connector: "Connect with MCP",
  watches: "Active Watches",
  account: "Account",
  notifications: "Notification Preferences",
  plans: "Pricing Plans & Billing",
  "live-options": "Live Options Flow",
  zdte: "0 DTE Options Flow",
};

const descriptionByTab: Record<PortalSkeletonTab, string> = {
  overview: "Preparing your market snapshot and watch activity.",
  connector: "Preparing setup guides and connector details.",
  watches: "Loading your active monitors and alert history.",
  account: "Loading profile, subscription, and usage details.",
  notifications: "Loading channels and saved alert preferences.",
  plans: "Loading billing status and available plans.",
  "live-options": "Loading real-time options trade data.",
  zdte: "Loading zero days to expiration trade data.",
};

const watchTableGridStyle: CSSProperties = {
  gridTemplateColumns:
    "minmax(300px, 2.25fr) minmax(180px, 1.35fr) minmax(130px, 1.35fr) minmax(110px, 1fr) minmax(150px, 0.95fr)",
};

const flowTableGridStyle: CSSProperties = {
  gridTemplateColumns:
    "minmax(100px, 0.85fr) minmax(120px, 1.1fr) minmax(80px, 0.75fr) minmax(100px, 0.95fr) minmax(110px, 1fr) minmax(100px, 0.9fr) minmax(120px, 1.1fr) minmax(100px, 0.9fr) minmax(80px, 0.7fr)",
};

function PageTitleSkeleton({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h1
        className="watches-page-title font-bold tracking-tight text-foreground"
        style={{ margin: 0, lineHeight: 1.1 }}
      >
        <span className="inline-flex items-center gap-2">
          {icon}
          {title}
        </span>
      </h1>
      <Mono size=".8rem" color={T.dim}>
        {description}
      </Mono>
    </div>
  );
}

function OverviewStatusBarSkeleton() {
  return (
    <div className="flex w-fit max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-full border border-border bg-card px-4 py-3">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="flex min-w-0 items-center gap-2"
        >
          <Skeleton width={16} height={16} borderRadius={999} />
          <SkeletonText width={item === 2 ? 112 : 72} height={13} />
        </div>
      ))}
    </div>
  );
}

function OverviewMetricCardSkeleton({
  title,
  chart = true,
}: {
  title: string;
  chart?: boolean;
}) {
  return (
    <Card
      style={{
        padding: 18,
        display: "grid",
        gap: 16,
        minHeight: 386,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="grid gap-4">
          <div className="text-base font-medium text-foreground">{title}</div>
          <SkeletonText width={92} height={22} />
        </div>
        <Skeleton width={10} height={10} borderRadius={999} />
      </div>

      {chart ? (
        <div className="rounded-2xl border border-border bg-secondary/40 p-3">
          <SkeletonChart height={106} style={{ borderRadius: 14 }} />
        </div>
      ) : (
        <div className="h-[106px]" />
      )}

      <div className="mt-auto grid gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="grid gap-2">
            <div className="flex items-center justify-between gap-4">
              <SkeletonText width={index === 0 ? 72 : 94} height={10} />
              <SkeletonText width={index === 2 ? 70 : 48} height={12} />
            </div>
            <Skeleton width="100%" height={7} borderRadius={999} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function getOverviewSkeletonDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getOverviewSkeletonGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning,";
  if (hour < 17) return "Good afternoon,";
  return "Good evening,";
}

function OverviewHeaderSkeleton() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-2">
        <Mono size={T.sub} color={T.dim}>
          {getOverviewSkeletonDate()}
        </Mono>
        <h1
          className="watches-page-title"
          style={{
            fontFamily: T.serif,
            fontWeight: 700,
            color: T.text,
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          <span className="inline-flex flex-wrap items-center gap-2">
            {getOverviewSkeletonGreeting()}
            <Skeleton width={118} height={28} borderRadius={6} />
          </span>
        </h1>
      </div>
      <Skeleton width={32} height={32} borderRadius={8} />
    </div>
  );
}

export function WatchTableSkeleton({ rows = 10 }: { rows?: number }) {
  const headers = ["Contract / Ticker", "Status / Intent", "Created", "Alerts", "Actions"];

  return (
    <>
      <SkeletonStyles />
      <div className="overflow-x-auto w-full">
        <div className="w-full min-w-[870px]">
          <div
            className="grid w-full gap-0 border-b border-border/50 bg-muted/30 px-6 py-3"
            style={watchTableGridStyle}
          >
            {headers.map((label) => (
              <div
                key={label}
                className={`text-[10px] font-bold uppercase tracking-wider text-muted-foreground ${
                  label === "Alerts" || label === "Actions" ? "text-right" : ""
                }`}
              >
                {label}
              </div>
            ))}
          </div>
          <div className="divide-y divide-border/40">
            {Array.from({ length: rows }).map((_, index) => (
              <div
                key={index}
                className="grid w-full items-center gap-0 px-6 py-4"
                style={watchTableGridStyle}
              >
                <div className="flex items-center gap-3">
                  <Skeleton width={36} height={36} borderRadius={8} />
                  <div className="grid gap-2">
                    <SkeletonText width={72} height={13} />
                    <SkeletonText width={155} height={10} />
                    <SkeletonText width={110} height={8} />
                  </div>
                </div>
                <div className="grid gap-2">
                  <SkeletonText width={76} height={18} />
                  <SkeletonText width={120} height={10} />
                </div>
                <div>
                  <SkeletonText width={82} height={12} />
                </div>
                <div className="justify-self-end">
                  <SkeletonText width={42} height={12} />
                </div>
                <div className="flex justify-end gap-2">
                  <Skeleton width={28} height={28} borderRadius={6} />
                  <Skeleton width={28} height={28} borderRadius={6} />
                  <Skeleton width={28} height={28} borderRadius={6} />
                  <Skeleton width={28} height={28} borderRadius={6} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 border-t border-border/50 px-6 py-5">
            <Skeleton width={76} height={32} borderRadius={8} />
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                width={32}
                height={32}
                borderRadius={8}
                style={index === 0 ? { opacity: 0.95 } : undefined}
              />
            ))}
            <Skeleton width={60} height={32} borderRadius={8} />
            <SkeletonText width={72} height={12} style={{ marginLeft: 8 }} />
          </div>
        </div>
      </div>
    </>
  );
}

export function FlowTableSkeleton({ rows = 10 }: { rows?: number }) {
  const headers = ["TIME", "SYMBOL", "TYPE", "STRIKE", "SIZE", "PRICE", "PREMIUM", "EXP", "ACTIONS"];

  return (
    <>
      <SkeletonStyles />
      <div className="overflow-x-auto w-full">
        <div className="w-full min-w-[920px]">
          <div className="grid w-full gap-0 border-b border-border/50 bg-muted/30 px-6 py-3" style={flowTableGridStyle}>
            {headers.map((header) => (
              <div
                key={header}
                className={`text-[10px] font-bold uppercase tracking-wider text-muted-foreground ${
                  ["STRIKE", "SIZE", "PRICE", "PREMIUM", "EXP", "ACTIONS"].includes(header) ? "text-right" : ""
                }`}
              >
                {header}
              </div>
            ))}
          </div>
          <div className="divide-y divide-border/40">
            {Array.from({ length: rows }).map((_, index) => (
              <div
                key={index}
                className="grid w-full items-center gap-0 px-6 py-4"
                style={flowTableGridStyle}
              >
                <SkeletonText width={62} height={11} />
                <div className="flex items-center gap-2">
                  <SkeletonText width={54} height={13} />
                  <Skeleton width={18} height={16} borderRadius={4} />
                </div>
                <Skeleton width={48} height={20} borderRadius={999} />
                <div className="justify-self-end">
                  <SkeletonText width={54} height={12} />
                </div>
                <div className="justify-self-end">
                  <SkeletonText width={62} height={12} />
                </div>
                <div className="justify-self-end">
                  <SkeletonText width={50} height={12} />
                </div>
                <div className="justify-self-end">
                  <SkeletonText width={76} height={12} />
                </div>
                <div className="justify-self-end">
                  <SkeletonText width={70} height={11} />
                </div>
                <div className="justify-self-end">
                  <Skeleton width={32} height={32} borderRadius={8} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center justify-between gap-4 border-t border-border/50 bg-muted/10 p-4 sm:flex-row">
            <SkeletonText width={168} height={12} />

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <SkeletonText width={58} height={10} />
                <Skeleton width={70} height={32} borderRadius={8} />
              </div>

              <div className="flex items-center gap-2">
                <Skeleton width={32} height={32} borderRadius={999} />
                <Skeleton width={54} height={28} borderRadius={999} />
                <Skeleton width={32} height={32} borderRadius={999} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function OverviewSkeleton() {
  return (
    <div className="fade-up flex min-w-0 max-w-full flex-col gap-5">
      <OverviewHeaderSkeleton />
      <OverviewStatusBarSkeleton />
      <div className="grid gap-4 md:grid-cols-3">
        <OverviewMetricCardSkeleton title="Market Regime" />
        <OverviewMetricCardSkeleton title="SPY" />
        <OverviewMetricCardSkeleton title="Options Flow" chart={false} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: T.serif }}>
          Market Brief
        </h2>
        <Skeleton width={92} height={36} borderRadius={8} />
      </div>
      <div className="flex flex-col gap-4">
        <Card style={{ padding: 22, display: "grid", gap: 16 }}>
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-2">
              <div
                style={{
                  fontFamily: T.serif,
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: T.text,
                }}
              >
                Today's Brief
              </div>
              <SkeletonText width={240} height={12} />
            </div>
            <Skeleton width={16} height={16} borderRadius={4} />
          </div>
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="grid gap-2 rounded-lg bg-secondary p-3">
                <SkeletonText width={60} height={9} />
                <SkeletonText width={`${index % 2 === 0 ? 72 : 88}%`} height={12} />
              </div>
            ))}
          </div>
        </Card>
        <Card style={{ padding: 22, display: "grid", gap: 14 }}>
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-2">
              <div
                style={{
                  fontFamily: T.serif,
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: T.text,
                }}
              >
                Market Tape
              </div>
              <SkeletonText width={230} height={12} />
            </div>
            <Skeleton width={16} height={16} borderRadius={4} />
          </div>
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="grid gap-2 rounded-lg border border-border p-3">
              <SkeletonText width={80} height={18} />
              <SkeletonText width={`${index % 2 === 0 ? 92 : 75}%`} height={12} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function WatchesSkeleton() {
  return (
    <div className="fade-up flex min-h-[88vh] min-w-0 max-w-full flex-col gap-6">
      <div className="watches-header">
        <PageTitleSkeleton title={titleByTab.watches} description={descriptionByTab.watches} />
        <div className="watches-header-actions">
          <Skeleton width={210} height={36} borderRadius={8} />
          <Skeleton width={120} height={40} borderRadius={999} />
        </div>
      </div>
      <div className="watches-filter-bar">
        <Skeleton width="100%" height={40} borderRadius={8} />
        <Skeleton width={92} height={12} />
      </div>
      <Card className="watches-table-shell">
        <WatchTableSkeleton />
      </Card>
    </div>
  );
}

function NotificationsSkeleton() {
  return (
    <main className="mx-auto min-h-[88vh] min-w-0 max-w-full">
      <div className="mb-12">
        <PageTitleSkeleton title={titleByTab.notifications} description={descriptionByTab.notifications} />
      </div>
      <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 items-start gap-3">
                <Skeleton width={44} height={44} borderRadius={8} />
                <div className="grid flex-1 gap-2">
                  <SkeletonText width={120} height={16} />
                  <SkeletonText width="78%" height={11} />
                </div>
              </div>
              <Skeleton width={42} height={24} borderRadius={999} />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <Skeleton width="80%" height={36} borderRadius={8} />
              <Skeleton width="20%" height={36} borderRadius={8} />
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-muted/40 p-6">
        <SkeletonText width={180} height={18} />
        <SkeletonText width="65%" height={12} style={{ marginTop: 10 }} />
      </div>
    </main>
  );
}

function AccountSkeleton() {
  return (
    <div className="fade-up flex min-h-[88vh] min-w-0 max-w-full flex-col gap-6">
      <div className="border-b border-border pb-6">
        <PageTitleSkeleton title={titleByTab.account} description={descriptionByTab.account} />
      </div>
      <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="grid gap-4">
          <SkeletonText width={150} height={18} />
          <div className="rounded-2xl border border-border/50 bg-card p-6">
            <div className="flex items-center gap-6">
              <Skeleton width={80} height={80} borderRadius={16} />
              <div className="grid gap-3">
                <SkeletonText width={190} height={24} />
                <SkeletonText width={240} height={13} />
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-8 border-t border-border/10 pt-6 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="grid gap-2">
                  <SkeletonText width={110} height={10} />
                  <SkeletonText width={index % 2 === 0 ? 150 : 190} height={14} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-8">
          <div className="rounded-lg border border-primary/10 bg-card p-6">
            <SkeletonText width={130} height={18} />
            <SkeletonText width="80%" height={11} style={{ marginTop: 8 }} />
            <div className="mt-6 grid gap-4">
              <SkeletonChart height={76} />
              <SkeletonText width="100%" height={36} />
              <SkeletonText width="100%" height={44} />
            </div>
          </div>
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6">
            <SkeletonText width={130} height={16} />
            <SkeletonText width="90%" height={12} style={{ marginTop: 14 }} />
            <SkeletonText width="100%" height={40} style={{ marginTop: 16 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PlansSkeleton() {
  return (
    <div className="fade-up flex min-w-0 max-w-full flex-col gap-5">
      <div className="rounded-xl border border-border bg-card p-5">
        <PageTitleSkeleton title={titleByTab.plans} description={descriptionByTab.plans} />
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <SkeletonText width="100%" height={58} />
          <SkeletonText width="100%" height={58} />
          <SkeletonText width="100%" height={58} />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-border bg-card p-6">
            <SkeletonText width={80} height={10} />
            <SkeletonText width={120} height={22} style={{ marginTop: 10 }} />
            <SkeletonText width={72} height={32} style={{ marginTop: 18 }} />
            <SkeletonChart height={80} style={{ marginTop: 18 }} />
            <div className="mt-5 grid gap-3">
              {Array.from({ length: 5 }).map((_, itemIndex) => (
                <SkeletonText key={itemIndex} width={itemIndex % 2 === 0 ? "92%" : "74%"} height={12} />
              ))}
            </div>
            <SkeletonText width="100%" height={44} style={{ marginTop: 24 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectorSkeleton() {
  return (
    <main className="min-w-0 max-w-full pb-16">
      <div className="grid gap-12 lg:grid-cols-[minmax(0,54rem)_1fr] lg:justify-between">
        <div>
          <header className="mb-12 border-b border-border pb-8">
            <Skeleton width={132} height={28} borderRadius={4} />
            <PageTitleSkeleton title={titleByTab.connector} description={descriptionByTab.connector} />
          </header>
          <section className="mb-12">
            <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-accent">
              Features
            </h2>
            <SkeletonText width={260} height={24} />
            <div className="mt-5 divide-y divide-border border-y border-border">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="py-4 md:grid md:grid-cols-[12rem_1fr] md:gap-6">
                  <SkeletonText width={130} height={14} />
                  <SkeletonText width={`${index % 2 === 0 ? 82 : 68}%`} height={12} />
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-accent">
              Setup
            </h2>
            <SkeletonText width={260} height={24} />
            <SkeletonText width="70%" height={14} style={{ marginTop: 12 }} />
            <div className="mt-8 rounded-sm border border-border bg-card p-5">
              <SkeletonText width={180} height={12} />
              <SkeletonText width="100%" height={16} style={{ marginTop: 16 }} />
            </div>
          </section>
        </div>
        <aside className="hidden lg:block">
          <div className="border-l border-border pl-5">
            <SkeletonText width={96} height={12} />
            <div className="mt-4 grid gap-3">
              <SkeletonText width={120} height={14} />
              <SkeletonText width={90} height={14} />
              <SkeletonText width={150} height={14} />
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function FlowSkeleton({ tab }: { tab: "live-options" | "zdte" }) {
  return (
    <div className="flex min-w-0 max-w-full flex-col space-y-6 font-sans">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <PageTitleSkeleton
          title={titleByTab[tab]}
          description={descriptionByTab[tab]}
          icon={<Zap className="h-5 w-5 text-primary" />}
        />
        <div className="flex items-center gap-3">
          <Skeleton width={112} height={36} borderRadius={999} />
          <Skeleton width={132} height={36} borderRadius={999} />
          <Skeleton width={36} height={36} borderRadius={999} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-3 md:flex-row">
        <Skeleton width="100%" height={40} borderRadius={8} />
        <div className="flex w-full gap-2 md:w-auto">
          <Skeleton width={104} height={40} borderRadius={8} />
          <Skeleton width={92} height={40} borderRadius={8} />
        </div>
      </div>
      <Card>
        <FlowTableSkeleton />
      </Card>
    </div>
  );
}

export function PortalPageSkeleton({ tabId }: { tabId: string }) {
  const tab = (tabId in titleByTab ? tabId : "overview") as PortalSkeletonTab;

  switch (tab) {
    case "overview":
      return <OverviewSkeleton />;
    case "watches":
      return <WatchesSkeleton />;
    case "notifications":
      return <NotificationsSkeleton />;
    case "account":
      return <AccountSkeleton />;
    case "plans":
      return <PlansSkeleton />;
    case "connector":
      return <ConnectorSkeleton />;
    case "live-options":
      return <FlowSkeleton tab="live-options" />;
    case "zdte":
      return <FlowSkeleton tab="zdte" />;
    default:
      return <OverviewSkeleton />;
  }
}
